import { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { Search, X, ChevronRight } from 'lucide-react';
import { ExerciseReadDTO, ExerciseSearchResult } from '@/types/exercise';
import { ExerciseBlock } from '@/types/workout';
import { exerciseApi, PaginatedSearchResponse } from '@/services/exerciseApi';
import { exerciseReadDTOToExerciseBlock } from '@/lib/transformers';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface AddExerciseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (exercise: ExerciseBlock) => void;
  currentExerciseCount: number;
  onExerciseAdded?: (exerciseName: string) => void;
}

export function AddExerciseDialog({
  isOpen,
  onClose,
  onAdd,
  currentExerciseCount,
  onExerciseAdded,
}: AddExerciseDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ExerciseSearchResult[]>([]);
  const [paginatedExercises, setPaginatedExercises] = useState<ExerciseReadDTO[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingExercises, setIsLoadingExercises] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<ExerciseReadDTO | null>(null);
  const itemsPerPage = 10;
  const hasInitialLoadRef = useRef(false);
  const prevSearchQueryRef = useRef('');

  // Load default exercises when dialog opens (using paginated endpoint)
  useEffect(() => {
    if (isOpen && searchQuery.trim().length < 2 && !hasInitialLoadRef.current) {
      const loadExercises = async () => {
        setIsLoadingExercises(true);
        try {
          const response = await exerciseApi.searchExercisesPaginated(undefined, itemsPerPage, null);
          setPaginatedExercises(response.results.map(item => item.exercise));
          setNextCursor(response.next_cursor);
          setHasMore(response.has_more);
          hasInitialLoadRef.current = true;
        } catch (error) {
          console.error('Failed to load exercises:', error);
        } finally {
          setIsLoadingExercises(false);
        }
      };
      loadExercises();
    }
  }, [isOpen, itemsPerPage]);

  // Search exercises when query changes
  useEffect(() => {
    const searchExercises = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        // Reload paginated exercises when search is cleared (but not on initial mount)
        const wasSearching = prevSearchQueryRef.current.trim().length >= 2;
        if (isOpen && wasSearching) {
          setIsLoadingExercises(true);
          try {
            const response = await exerciseApi.searchExercisesPaginated(undefined, itemsPerPage, null);
            setPaginatedExercises(response.results.map(item => item.exercise));
            setNextCursor(response.next_cursor);
            setHasMore(response.has_more);
          } catch (error) {
            console.error('Failed to load exercises:', error);
          } finally {
            setIsLoadingExercises(false);
          }
        }
        prevSearchQueryRef.current = searchQuery;
        return;
      }

      prevSearchQueryRef.current = searchQuery;

      setIsSearching(true);
      try {
        // Use keyword search endpoint for search queries
        const results = await exerciseApi.searchExercises(searchQuery.trim(), 100);
        setSearchResults(results);
      } catch (error) {
        console.error('Failed to search exercises:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce search
    const timeoutId = setTimeout(searchExercises, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, isOpen, itemsPerPage]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults([]);
      setPaginatedExercises([]);
      setNextCursor(null);
      setHasMore(false);
      setSelectedExercise(null);
      hasInitialLoadRef.current = false;
      prevSearchQueryRef.current = '';
    }
  }, [isOpen]);

  // Load next page of paginated exercises
  const loadNextPage = async () => {
    if (!nextCursor || searchQuery.trim().length >= 2) return;
    
    setIsLoadingExercises(true);
    try {
      const response = await exerciseApi.searchExercisesPaginated(undefined, itemsPerPage, nextCursor);
      setPaginatedExercises(prev => [...prev, ...response.results.map(item => item.exercise)]);
      setNextCursor(response.next_cursor);
      setHasMore(response.has_more);
    } catch (error) {
      console.error('Failed to load next page:', error);
    } finally {
      setIsLoadingExercises(false);
    }
  };

  // Get exercises to display
  const exercisesToDisplay = searchQuery.trim().length >= 2 && searchResults.length > 0
    ? searchResults.map(result => result.exercise)
    : paginatedExercises;

  const handleSelectExercise = (exercise: ExerciseReadDTO) => {
    setSelectedExercise(exercise);
  };

  const handleAddExercise = async () => {
    if (!selectedExercise) return;

    const exerciseBlock = exerciseReadDTOToExerciseBlock(
      selectedExercise,
      currentExerciseCount
    );
    onAdd(exerciseBlock);
    
    // Notify parent about the added exercise
    if (onExerciseAdded) {
      onExerciseAdded(selectedExercise.name);
    }
    
    // Reset state but keep modal open
    // Note: Clearing searchQuery will trigger the useEffect to reload exercises automatically
    setSelectedExercise(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[80vh] flex flex-col"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">Add Exercise</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-neutral-600" />
          </button>
        </div>

        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <Input
              type="text"
              placeholder="Search exercises..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto mb-4 min-h-[300px]">
          {isLoadingExercises && (
            <div className="text-center py-8 text-neutral-500">Loading exercises...</div>
          )}

          {!isLoadingExercises && isSearching && (
            <div className="text-center py-8 text-neutral-500">Searching...</div>
          )}

          {!isLoadingExercises && !isSearching && exercisesToDisplay.length === 0 && (
            <div className="text-center py-8 text-neutral-500">
              {searchQuery.trim().length >= 2 ? 'No exercises found' : 'No exercises available'}
            </div>
          )}

          {!isLoadingExercises && !isSearching && exercisesToDisplay.length > 0 && (
            <div className="space-y-2">
              {exercisesToDisplay.map((exercise) => {
                const isSelected = selectedExercise?.id === exercise.id;
                
                return (
                  <button
                    key={exercise.id}
                    onClick={() => handleSelectExercise(exercise)}
                    className={`w-full text-left p-4 border rounded-lg transition-all ${
                      isSelected
                        ? 'border-neutral-900 bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-400 hover:bg-neutral-50'
                    }`}
                  >
                    <div className="font-medium text-neutral-900">{exercise.name}</div>
                    {exercise.description && (
                      <div className="text-sm text-neutral-600 mt-1">
                        {exercise.description}
                      </div>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-neutral-500">
                      {exercise.muscle_group && (
                        <span>Muscle: {exercise.muscle_group}</span>
                      )}
                      {exercise.equipment && (
                        <span>Equipment: {exercise.equipment}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Load More button for paginated results */}
          {!isLoadingExercises && !isSearching && searchQuery.trim().length < 2 && hasMore && (
            <div className="mt-4 flex justify-center">
              <Button
                onClick={loadNextPage}
                disabled={isLoadingExercises}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                Load More
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        <div className="flex gap-3 justify-end border-t pt-4">
          <Button onClick={onClose} variant="outline" size="sm">
            Cancel
          </Button>
          <Button
            onClick={handleAddExercise}
            variant="default"
            size="sm"
            disabled={!selectedExercise}
          >
            Add Exercise
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

